import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IconButton } from "@nexus/ui";

/**
 * Horizontal scroll-snap carousel with edge-aware nav buttons. Mirrors
 * `webui/app/src/pages/measurement/components/Carousel.tsx` — duplicated
 * here rather than imported because widgets cannot depend on app/src.
 */
export function Carousel({ children }: { children: ReactNode }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState({ hasOverflow: false, atStart: true, atEnd: true });

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const updateScrollState = () => {
      const hasOverflow = track.scrollWidth > track.clientWidth + 1;
      setScrollState({
        hasOverflow,
        atStart: track.scrollLeft <= 1,
        atEnd: track.scrollLeft + track.clientWidth >= track.scrollWidth - 1,
      });
    };

    updateScrollState();
    track.addEventListener("scroll", updateScrollState, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(track);

    return () => {
      track.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [children]);

  const scrollBy = (direction: 1 | -1) => {
    trackRef.current?.scrollBy({ left: direction * 340, behavior: "smooth" });
  };

  return (
    <div className="relative">
      {scrollState.hasOverflow ? (
        <IconButton
          label="Previous items"
          variant="secondary"
          size="md"
          disabled={scrollState.atStart}
          onClick={() => scrollBy(-1)}
          className="absolute -left-2 top-1/2 z-10 -translate-y-1/2 bg-surface/95 shadow-sm"
        >
          <ChevronLeft className="h-4 w-4" />
        </IconButton>
      ) : null}
      <div
        ref={trackRef}
        className="flex snap-x snap-proximity gap-3 overflow-x-auto scroll-smooth pb-2 [scrollbar-width:thin] [&>*]:snap-start"
      >
        {children}
      </div>
      {scrollState.hasOverflow ? (
        <IconButton
          label="Next items"
          variant="secondary"
          size="md"
          disabled={scrollState.atEnd}
          onClick={() => scrollBy(1)}
          className="absolute -right-2 top-1/2 z-10 -translate-y-1/2 bg-surface/95 shadow-sm"
        >
          <ChevronRight className="h-4 w-4" />
        </IconButton>
      ) : null}
    </div>
  );
}
